import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectPatientResult } from '../features/patient/patientSlice';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PatientData, PatientStatusEnum } from '../models/PatientModels';
import * as psTokens from '../global/PubSubTokens';
import { SubMenuEvents } from '../global/PubSubTokens';
import { AiOutlineAppstore, AiOutlineUser} from 'react-icons/all';
import { Tooltip } from 'antd';
import { publish } from '../components/Layout/GlobalEvents';
import { InfoCircleOutlined } from '@ant-design/icons';
import StatusPill from '../components/Utils/StatusPill';
import { usePatientDetailsMutation } from '../features/patient/patientService';
import Icons from '../components/icons/HrxIcons';

const usePatientMenu = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = useLocation();
    const { id: patientId, medicationId='medId', orderId='oId' } = useParams();

    const [patientSubMenu, setPatientSubMenu] = useState<[]>([]);
    const [patientSubMenuDic, setPatientSubMenuDic] = useState<{}>({});
    const [patientRoutes, setPatientRoutes] = useState<string[]>([]);

    const [patientDetails] = usePatientDetailsMutation();
    const patient_details = useSelector(selectPatientResult, shallowEqual);
    let data: PatientData | undefined | any = patient_details?.patientDetails;

    const getPatientDetails = async (_id: any) => {
        try {
            return await patientDetails(_id);
        } catch (error) {
            return error;
        }
    };

    const getPatientData = async (patientId: any) => {
        const response: any = await getPatientDetails(patientId);
        data = response.data;
    };

    const getLinkScriptSureRelated = (data: any, link: string, noLink: string | undefined = undefined) => {
        if (!data?.allergiesConfirmed || !data?.consent) {
            return noLink;
        }
        return link;
    };

    useEffect(() => {
        if (data?.patientId == undefined && patientId != undefined) {
            getPatientData(patientId);
        }
    }, [data, patientId, location, pathname]);

    //https://uat.naviscript.com/patient/e735b450-e8ec-4b52-d128-08dd7ac865c3/pending-order/d330d534-a495-4167-9952-08dd8e2ea930
    useEffect(() => {
        if (patientId != undefined) {
            setPatientRoutes([
                `/patient/${patientId}`,
                `/patient/${patientId}/diagnosis`,
                `/patient/${patientId}/allergies`,
                `/patient/${patientId}/add-new-medication`,
                `/patient/${patientId}/approve-medications/view`,
                `/patient/${patientId}/approve-medications/submitAll`,
                `/patient/${patientId}/med-order-create`,
                `/patient/${patientId}/prescriptions`,
                `/patient/${patientId}/update-medication/${orderId}`,
                `/patient/${patientId}/add-approve-medication/${medicationId}/${orderId}`,
                `/patient/${patientId}/pending-order/${orderId}`,
                `/patient/${patientId}/medications-history`,
                `/patient/${patientId}/re-admit`,
                `/patient-discharge/${patientId}`,
                `/patient_record/${patientId}`
            ]);
        }
    }, [patientId, medicationId, orderId, location]);

    useEffect(() => {
        if (data?.patientId != undefined && patientRoutes.includes(pathname)) {

            const baseUrl = `/patient/${patientId}`;
            const {patientStatus, consent} = data;

            setPatientSubMenuDic({
                'PatientInformation': [`/patient/${patientId}`,`/patient_record/${patientId}`],
                'PatientDiagnosis': [`/patient/${patientId}/diagnosis`],
                'PatientAllergies': [`/patient/${patientId}/allergies`],
                'AddNewMed':[ `/patient/${patientId}/add-new-medication`, `/patient/${patientId}/med-order-create`, `/patient/${patientId}/update-medication/${orderId}`],
                'ReviewMedication': [`/patient/${patientId}/approve-medications/view`, `/patient/${patientId}/add-approve-medication/${medicationId}/${orderId}`, `/patient/${patientId}/approve-medications/submitAll`],
                'MedSheetsRe-Prescribe': [`/patient/${patientId}/medications-history`, `/patient/${patientId}/prescriptions`, `/patient/${patientId}/pending-order/${orderId}`],
                'Re-Admit': [`/patient/${patientId}/re-admit`],
                'Discharge-Patient': [`/patient-discharge/${patientId}`]
            });

            const patientDiagnosisUrl = consent ? `${baseUrl}/diagnosis` : `#`;
            const patientAllergies = consent ? `${baseUrl}/allergies` : `#`;

            const addNewMedUrl = getLinkScriptSureRelated( data, `${baseUrl}/add-new-medication`, '#' );
            const reviewMedicationUrl = getLinkScriptSureRelated(data,`${baseUrl}/approve-medications/view`,'#');
            const medSheetsRePrescribeUrl = getLinkScriptSureRelated(data, `${baseUrl}/medications-history`,'#');

            const subMenu: any = [
                {
                    key: 'DiagnosisAndAllergies',
                    label: 'Diagnosis and Allergies',
                    type: 'group'
                },
                {
                    key: 'PatientInformation',
                    icon: <AiOutlineUser />,
                    label: 'Patient Information',
                    to: `/patient/${patientId}`,
                },
                {
                    key: 'PatientDiagnosis',
                    icon: <Icons name='PatientDiagnosis'/>,
                    label: 'Patient Diagnosis',
                    to: patientDiagnosisUrl,
                    eventType: psTokens.EventTypeConsentModalOpen,
                    eventPayload: true,
                },
                {
                    key: 'PatientAllergies',
                    icon: <Icons name='PatientAllergies'/>,
                    label: (
                        <div className="d-flex align-items-center gap-2 px-2">
                            Patient Allergies
                            <StatusPill
                                style={{ height: '20px' }}
                                className={
                                    data?.allergiesConfirmed
                                        ? 'allergy-complete d-flex align-items-center'
                                        : 'allergy-pending d-flex align-items-center'
                                }
                                title={
                                    data?.allergiesConfirmed
                                        ? 'complete'
                                        : 'incomplete'
                                }
                            />
                        </div>
                    ),
                    to: patientAllergies,
                    eventType: psTokens.EventTypeConsentModalOpen,
                    eventPayload: true,
                },
                {
                    key: 'Medications',
                    label: (
                        <div className="d-flex align-items-center gap-2 ">
                            <Tooltip
                                placement="right"
                                color="#ffffff"
                                title={
                                    <span className="side-item-link-color tt-fs">
                                        Access &nbsp;
                                        <span
                                            className="text-decoration-underline cursor-pointer"
                                            onClick={() => {
                                                if(pathname !== `/patient/${patientId}`)
                                                    navigate(`/patient/${patientId}`, { state: { type: psTokens.EventTypeClickEPrescribe, payload:data } });
                                                else{
                                                    publish(SubMenuEvents, {
                                                        type: psTokens.EventTypeClickEPrescribe,
                                                        payload: data,
                                                    });
                                                }
                                            }} >
                                            Legacy ePrescribe
                                        </span>
                                    </span>
                                }>
                                <InfoCircleOutlined rev={undefined} />
                            </Tooltip>
                            <div className="ant-menu-item-group-title group-text ps-1" title="Medications">Medications</div>
                        </div>
                    ),
                    type: 'group',
                    eventType: psTokens.EventTypeClickEPrescribe,
                    eventPayload: data,
                },
                {
                    key: 'AddNewMed',
                    icon: <Icons name='AddNewMed'/>,
                    label: 'Add New Medication',
                    to: addNewMedUrl,
                    eventType: psTokens.EventTypeScriptSureRelatedAddNewMedication,
                    eventPayload: data,
                },
                {
                    key: 'ReviewMedication',
                    icon: <Icons name='ReviewMedication'/>,
                    label: 'Review Medication',
                    to: reviewMedicationUrl,
                    eventType: psTokens.EventTypeScriptSureRelatedApproveMedicationsView,
                    eventPayload: data,
                },
                {
                    key: 'MedSheetsRe-Prescribe',
                    icon: <Icons name='MedHistory'/>,
                    label: 'Med Sheets & Re-Prescribe',
                    to: medSheetsRePrescribeUrl,
                    eventType: psTokens.EventTypeScriptSureRelatedMedicationsHistory,
                    eventPayload: data,
                },
                {
                    key: 'ManagePatient',
                    label: 'Manage Patient',
                    type: 'group',
                },
            ];
            if (patientStatus === PatientStatusEnum.Admitted) {
                subMenu.push({
                    key: 'Discharge-Patient',
                    icon: <Icons name='Discharge'/>,
                    label: 'Discharge Patient',
                    to: `/patient-discharge/${patientId}`,
                    isPatientMenu: true,
                });
            }
            if (
                patientStatus === PatientStatusEnum.Invalid ||
                patientStatus === PatientStatusEnum.DischargedDeceased ||
                patientStatus === PatientStatusEnum.DischargedRevoked ||
                patientStatus === PatientStatusEnum.DischargedTransferred ||
                patientStatus === PatientStatusEnum.DischargedOther
            ) {
                subMenu.push({
                    key: 'Re-Admit',
                    icon: <AiOutlineAppstore />,
                    label: 'Re-Admit',
                    to: `/patient/${patientId}/re-admit`,
                    isPatientMenu: true,
                });
            }
            setPatientSubMenu(subMenu);
        } else {
            setPatientSubMenu([]);
            setPatientSubMenuDic({});
        }
    }, [data, patientId, medicationId, orderId, location, patientRoutes]);

    return { patientSubMenu, patientSubMenuDic };
};

export default usePatientMenu;
